import { Team } from '@yandex-int/k-common/typings';
import { Button, H3, Modal, ModalCloseProps, SimpleText, Text } from 'platform-components';
import { CloseButton } from 'platform-components/src/components/close-button/_view/close-button_view_outer';
import { SUBJECTS_SLUGS_TYPE } from 'platform-components/src/typings';
import * as React from 'react';
import { injectIntl, IntlShape } from 'react-intl';
import { connect } from 'react-redux';
import ym from 'react-yandex-metrika';
import { bindActionCreators, compose, Dispatch } from 'redux';

import { UMKHintData } from 'common.components/typings';
import { getUMKHintData } from 'common.components/umk/selectors';
import * as userActions from 'common.components/user/actions';
import { getCurrentClass } from 'common.components/user/selectors';
import { UMK_DATA } from 'common.components/user/user.constants';
import { getTeamIdFromPath } from 'utils/path';

import { ReduxTeacherStore, RootAction } from '../../typings';

import SchoolbookSuggest from './__schoolbook-suggest/umk-modal__schoolbook-suggest';
import { getSelectedSchoolbooks, getSubjectsWithUMK } from './selectors';
import { MAX_CLASS_NAME_LENGTH, MS_IN_DAY } from './typings';

import './umk-modal.scss';

interface StoreProps {
    canSave: boolean;
    classData: Team | null;
    umkHints: UMKHintData;
    selectedSchoolbooks: Record<SUBJECTS_SLUGS_TYPE, string>;
    subjectsWithUMK: Array<string>;
}

interface DispatchProps {
    addViewedHint: (hintKey: string, hintValue: any) => void;
}

interface UMKModalProps extends StoreProps, DispatchProps {
    intl: IntlShape;
}

interface UmkModalState {
    opened: boolean;
}

export class UMKModalComponent extends React.Component<UMKModalProps, UmkModalState> {
    state = {
        opened: true,
    };

    handleAcceptClick = () => {
        const { selectedSchoolbooks } = this.props;

        this.saveUmkData(selectedSchoolbooks);

        ym('reachGoal', 'teacher_onboarding_book_choose');
    };

    handleCancel = () => {
        this.saveUmkData(null);

        ym('reachGoal', 'teacher_onboarding_book_skip');
    };

    saveUmkData = (schoolbooks: { [subject: string]: string | null } | null) => {
        const { addViewedHint, umkHints } = this.props;

        const { id: classId, level: classNumber } = this.props.classData as Team;

        const classUMKData = umkHints[classId] || {};
        const now = new Date();
        // @ts-ignore
        let { closedAfterSeenCount = 0, lastSeen = now.toISOString() } = classUMKData;
        const lastSeenMoreThanDayAgo = now.getTime() - new Date(lastSeen).getTime() > MS_IN_DAY;

        if (lastSeenMoreThanDayAgo) {
            lastSeen = now.toISOString();
            closedAfterSeenCount = 0;
        }

        addViewedHint(UMK_DATA, {
            ...umkHints,
            [classId]: {
                lastSeen,
                closedAfterSeenCount: closedAfterSeenCount + 1,
                classNumber,
                schoolbooks,
            },
        });

        this.setState({ opened: false });
    };

    render() {
        const { canSave, classData, subjectsWithUMK } = this.props;
        const { opened } = this.state;

        const hasClassData = Boolean(classData && classData.level && classData.name);
        let classHeaderId, className;

        if (hasClassData) {
            const { name, level } = classData as Team;
            const trimmedName =
                name.length > MAX_CLASS_NAME_LENGTH ? `${name.substring(0, MAX_CLASS_NAME_LENGTH)}...` : name;
            className = `${level} «${trimmedName}»`.replace(/ /g, '\u00a0');
            classHeaderId = trimmedName.length === 1 ? 'UMKModal.header.withoutClass' : 'UMKModal.header.withClass';
        } else {
            className = '';
            classHeaderId = 'UMKModal.header.withClass';
        }

        return (
            <Modal
                //@ts-ignore - EDUCATION-37077: [react-update]: fixme typings: Хочет ReactNode вместo JSX.Element
                Close={(closeProps: ModalCloseProps) => (
                    <CloseButton {...closeProps} position={CloseButton.POSITION.ABSOLUTE} />
                )}
                autoclosable={false}
                hasClose
                mix="umk-modal"
                onRequestClose={this.handleCancel}
                opened={opened}
                theme={Modal.THEMES.TEACHER}
                useScale={false}
            >
                <Modal.Header>
                    <H3 id={classHeaderId} type={Text.TYPE.MARKDOWN} values={{ className }} weight={Text.WEIGHT.M} />
                </Modal.Header>
                <Modal.Content mix="umk-modal__content">
                    {subjectsWithUMK.map((subject) => (
                        <div className="umk-modal__form-field-wrap umk-modal__select" key={subject}>
                            <SchoolbookSuggest subject={subject} />
                        </div>
                    ))}
                </Modal.Content>
                <Modal.Footer>
                    <Button
                        className="umk-modal__form-field-wrap umk-modal__button"
                        disabled={!canSave}
                        onClick={this.handleAcceptClick}
                    >
                        <SimpleText id="UMKModal.form.accept" />
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    }
}

const mapStateToProps = (storeState: ReduxTeacherStore): StoreProps => {
    const selectedSchoolbooks = getSelectedSchoolbooks(storeState);
    const canSave = Object.values(selectedSchoolbooks).some((schoolbook) => Boolean(schoolbook));
    const classId = getTeamIdFromPath();

    return {
        selectedSchoolbooks,
        subjectsWithUMK: getSubjectsWithUMK(storeState),
        canSave,
        classData: getCurrentClass(storeState, { classId }),
        umkHints: getUMKHintData(storeState),
    };
};

const mapDispatchToProps = (dispatch: Dispatch<RootAction>): DispatchProps => {
    const { addViewedHint } = userActions;

    return bindActionCreators(
        {
            addViewedHint,
        },
        dispatch
    );
};

export const UMKModal = compose(connect(mapStateToProps, mapDispatchToProps), injectIntl)(UMKModalComponent);
